import { FC, useMemo } from 'react'
import axios from 'axios'
import { useQuery } from 'react-query'
import { useAuth } from '../../../modules/auth'
import { ChartWrapper, TOKENS } from '../../../components/design-system'

const API_URL = process.env.REACT_APP_API_URL

type Heatmap = Array<{ dow: number; hour: number; count: number }>

type StatsPayload = {
  period_days: number
  total_alarm_events: number
  affected_boxes: number
  top_boxes: Array<{ box_id: number; cmb_number: string | null; count: number }>
  heatmap: Heatmap
  by_type: Record<string, number>
}

const ALARM_LABEL: Record<string, string> = {
  door: 'Porta',
  bolt: 'Trava',
  door_lock: 'Fechadura',
  vibration: 'Vibração',
  energy: 'Energia',
  sound: 'Som',
  violation: 'Violação',
  request_opening_of_safe: 'Cofre',
  maximum_time_for_open_door: 'Porta aberta',
  cable_cut_alarm: 'Cabo cortado',
}

async function fetchDashboardStats(companyId?: number, days = 30): Promise<StatsPayload> {
  const { data } = await axios.get<{ data: StatsPayload }>(
    `${API_URL}/sensors/dashboard-stats`,
    { params: { company_id: companyId, days } },
  )
  return data.data
}

// Heatmap renderer — 7 rows × 24 cols. Each cell color goes from
// surface → action proportional to count vs max. Hover shows the count.
const DOW_LABEL = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function HeatmapGrid({ data }: { data: Heatmap }) {
  const max = useMemo(() => {
    let m = 0
    for (const c of data) if (c.count > m) m = c.count
    return m
  }, [data])

  // Convert UTC hour stored in DB to local hour for display. We do a
  // best-effort offset by reading the browser's current UTC offset; the
  // grid is shifted in the column dimension so the user reads local time.
  const offsetH = new Date().getTimezoneOffset() / 60 // positive = behind UTC
  const localData = useMemo(() => {
    return data.map((c) => {
      let hLocal = c.hour - offsetH
      hLocal = ((hLocal % 24) + 24) % 24
      return { ...c, hour: hLocal }
    })
  }, [data, offsetH])

  // Re-bucket because two UTC cells may now map to the same local cell.
  const grid = useMemo(() => {
    const g: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0))
    for (const c of localData) g[c.dow][c.hour] += c.count
    return g
  }, [localData])

  const cellSize = 14
  const colorFor = (count: number) => {
    if (count === 0) return TOKENS.color.divider
    const ratio = max > 0 ? count / max : 0
    // Lerp #dbeafe → #2563eb based on density.
    // Tailwind blue-100 → blue-600 hand-tuned.
    const lerp = (a: number, b: number) => Math.round(a + (b - a) * ratio)
    const r = lerp(0xdb, 0x25)
    const g = lerp(0xea, 0x63)
    const b = lerp(0xfe, 0xeb)
    return `rgb(${r}, ${g}, ${b})`
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'inline-grid', gridTemplateColumns: `auto repeat(24, ${cellSize}px)`, gap: 3, padding: '4px 0' }}>
        {/* header row: hour labels */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={`h-${h}`} style={{ fontSize: 9, color: TOKENS.color.muted, textAlign: 'center', lineHeight: `${cellSize}px` }}>
            {h % 3 === 0 ? h : ''}
          </div>
        ))}
        {DOW_LABEL.map((label, d) => (
          <>
            <div key={`d-${d}`} style={{ fontSize: 10, color: TOKENS.color.muted, paddingRight: 6, lineHeight: `${cellSize}px`, textAlign: 'right' }}>{label}</div>
            {grid[d].map((count, h) => (
              <div
                key={`c-${d}-${h}`}
                title={`${DOW_LABEL[d]} ${String(h).padStart(2, '0')}:00 — ${count} alarme(s)`}
                style={{
                  width: cellSize, height: cellSize,
                  background: colorFor(count),
                  borderRadius: 2,
                }}
              />
            ))}
          </>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 10, color: TOKENS.color.muted }}>
        Menos
        <div style={{ width: 12, height: 12, background: TOKENS.color.divider, borderRadius: 2 }} />
        <div style={{ width: 12, height: 12, background: '#bfdbfe', borderRadius: 2 }} />
        <div style={{ width: 12, height: 12, background: '#60a5fa', borderRadius: 2 }} />
        <div style={{ width: 12, height: 12, background: '#2563eb', borderRadius: 2 }} />
        Mais
      </div>
    </div>
  )
}

function TopBoxes({ items }: { items: StatsPayload['top_boxes'] }) {
  if (!items.length) {
    return (
      <div style={{ color: TOKENS.color.muted, fontSize: 12, padding: '12px 0' }}>
        Sem alarmes na janela selecionada.
      </div>
    )
  }
  const max = items[0]?.count ?? 1
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((b) => (
        <div key={b.box_id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 60, fontSize: 12, fontWeight: 700, color: TOKENS.color.text }}>
            CMB {b.cmb_number ?? '—'}
          </div>
          <div style={{ flex: 1, height: 8, background: TOKENS.color.divider, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${(b.count / max) * 100}%`,
              background: TOKENS.color.action,
              borderRadius: 4,
            }} />
          </div>
          <div style={{ width: 40, fontSize: 12, color: TOKENS.color.muted, textAlign: 'right' }}>
            {b.count}
          </div>
        </div>
      ))}
    </div>
  )
}

export const DashboardStats: FC = () => {
  const { currentUser } = useAuth()
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined

  const { data } = useQuery(
    ['dashboard-stats', companyId, 30],
    () => fetchDashboardStats(companyId, 30),
    {
      enabled: companyId !== undefined && companyId > 0,
      refetchOnWindowFocus: false,
      // Refetch every 2 minutes — heatmap doesn't need to be live, the
      // bell handles real-time. Lighter on the DB than the alarmed-boxes
      // query.
      refetchInterval: 120_000,
      keepPreviousData: true,
    },
  )

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: TOKENS.spacing.md,
    }}>
      <ChartWrapper label='Alarmes por hora · últimos 30 dias' minHeight={220}>
        {!data && <div style={{ color: TOKENS.color.muted, fontSize: 12 }}>Carregando…</div>}
        {data && data.total_alarm_events === 0 && (
          <div style={{ color: TOKENS.color.muted, fontSize: 13 }}>
            Sem alarmes no período. 👌
          </div>
        )}
        {data && data.total_alarm_events > 0 && <HeatmapGrid data={data.heatmap} />}
      </ChartWrapper>

      <ChartWrapper label='Top caixas mais alarmadas · 30 dias' minHeight={220}>
        {!data && <div style={{ color: TOKENS.color.muted, fontSize: 12 }}>Carregando…</div>}
        {data && (
          <>
            <div style={{
              display: 'flex',
              gap: TOKENS.spacing.lg,
              marginBottom: TOKENS.spacing.md,
              paddingBottom: TOKENS.spacing.sm,
              borderBottom: `1px solid ${TOKENS.color.divider}`,
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: TOKENS.color.muted }}>
                  Eventos
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text }}>
                  {data.total_alarm_events.toLocaleString('pt-BR')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: TOKENS.color.muted }}>
                  Caixas afetadas
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text }}>
                  {data.affected_boxes.toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
            <TopBoxes items={data.top_boxes} />
            {Object.values(data.by_type).some((c) => c > 0) && (
              <div style={{ marginTop: TOKENS.spacing.md, paddingTop: TOKENS.spacing.sm, borderTop: `1px solid ${TOKENS.color.divider}`, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {Object.entries(data.by_type)
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([k, n]) => (
                    <span key={k} style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '3px 8px',
                      borderRadius: TOKENS.radius.chip,
                      background: TOKENS.color.surface,
                      color: TOKENS.color.muted,
                      border: `1px solid ${TOKENS.color.border}`,
                    }}>
                      {ALARM_LABEL[k] ?? k} · {n}
                    </span>
                  ))}
              </div>
            )}
          </>
        )}
      </ChartWrapper>
    </div>
  )
}
